import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  EM_DASH,
  formatDate,
  formatNumber,
  formatUtcDateTime,
  hfAlpha,
  HFBox,
  HFButton,
  HFCard,
  HFCheckbox,
  HFChip,
  HFDataTable,
  HFSelect,
  HFSkeleton,
  HFTextField,
  HFTooltip,
  shortMonthName,
  type GridRowId,
  type HFChipTone,
  type HFDataTableRowTree,
  type HFGridColDef,
} from '@hf/designSystem/v1';
import { OMS_UNKNOWN_LABEL, omsOrderListSchema, omsOrderStatusLabel, type OmsAllocation, type OmsOrder, type OmsOrderList } from '@hf/sharedContract';
import { useHttp } from '@core/utils/http';
import { monitorPaneSx } from '../layout/monitorPane';
import { TableSkeleton } from './TableSkeleton';

type CheckGroup = 'PASS' | 'WARN' | 'BLOCK';

type CheckView = { group: CheckGroup; label: string; tone: HFChipTone };

type AllocationRow = {
  id: string;
  kind: 'allocations';
  orderId: number;
  qty: number;
  allocations: OmsAllocation[];
};

type TicketRow = {
  id: string;
  kind: 'ticket';
  children: AllocationRow[];
  ticket: OmsOrder;
  orderId: number;
  orderNumber: string;
  symbol: string;
  contract: string;
  contractHint?: string;
  side: string;
  qty: number;
  orderType: string;
  limit: string;
  tif: string;
  brokerAccount: string;
  status: string;
  check: CheckView;
  selectable: boolean;
};

type PreviewRow = TicketRow | AllocationRow;

const COMPACT_BUTTON_SX = { minHeight: 28, px: 1.25, py: 0.25, fontSize: 12 } as const;
/** 32px fields, matching the Trade Blotter filters. */
const FILTER_FIELD_SX = {
  '& .MuiInputBase-root': { height: 32, fontSize: 13 },
  '& .MuiInputBase-input': { py: 0, pl: 1.5 },
  '& .MuiInputLabel-root': { fontSize: 13 },
  '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': { transform: 'translate(12px, 7px) scale(1)' },
  '& .MuiInputLabel-shrink': { transform: 'translate(12px, -7px) scale(0.75)' },
  '& .MuiSelect-icon': { right: 6, fontSize: 18 },
} as const;
const CHROME_SX = { display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0, containerType: 'inline-size' } as const;
const CHROME_ROW_SX = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' } as const;
const CLUSTER_SX = { display: 'flex', alignItems: 'center', gap: 1 } as const;
const FILTER_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: 'repeat(6, minmax(140px, 1fr))',
  columnGap: 1,
  rowGap: 2,
  alignItems: 'center',
  '@container (max-width: 1052px)': { gridTemplateColumns: 'repeat(3, minmax(140px, 1fr))' },
  '@container (max-width: 600px)': { gridTemplateColumns: 'repeat(2, minmax(140px, 1fr))' },
} as const;
const SECTION_LABEL_SX = { fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' } as const;
const TICKET_ROW_PX = 36;
const PANEL_CHROME_PX = 92;
const PANEL_LINE_PX = 26;
const SIDE_LABELS = { BUY: 'Buy', SELL: 'Sell' } as const;
/** Exchange futures month codes, January to December. */
const FUTURES_MONTH_CODES = 'FGHJKMNQUVXZ';
const CHECK_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'PASS', label: 'Pass' },
  { value: 'WARN', label: 'Warn' },
  { value: 'BLOCK', label: 'Block' },
];
const SIDE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'BUY', label: 'Buy' },
  { value: 'SELL', label: 'Sell' },
];
const WRITE_STEP = 'Switches on with the first writes on HF Demo.';

function dash(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? '';
  return trimmed || EM_DASH;
}

/** Only Pass and Warn can be confirmed. Not checked and an unknown result fail closed as Block. */
function checkView(ticket: OmsOrder): CheckView {
  if (ticket.check === 'PASS') return { group: 'PASS', label: 'Pass', tone: 'pass' };
  if (ticket.check === 'WARN') return { group: 'WARN', label: 'Warn', tone: 'warn' };
  if (ticket.check === 'BLOCK') return { group: 'BLOCK', label: 'Block', tone: 'block' };
  if (ticket.check === 'NOT_CHECKED') return { group: 'BLOCK', label: 'Not checked', tone: 'block' };
  return { group: 'BLOCK', label: OMS_UNKNOWN_LABEL, tone: 'block' };
}

/** `Z` and `2026` read Z6, with Dec 2026 in the tooltip. */
function contractCell(ticket: OmsOrder): { contract: string; contractHint?: string } {
  if (ticket.isSpread) return { contract: 'Spread', contractHint: ticket.contractMonthCode };
  const code = ticket.contractMonthCode?.trim().toUpperCase() ?? '';
  const year = ticket.contractYear?.trim() ?? '';
  const month = code.length === 1 ? FUTURES_MONTH_CODES.indexOf(code) : -1;
  if (month < 0 || !year) return { contract: dash(code) };
  return { contract: `${code}${year.slice(-1)}`, contractHint: `${shortMonthName(month)} ${year}` };
}

function brokerName(broker: string | undefined): string | undefined {
  return broker?.trim().toUpperCase() === 'ADMSI' ? 'ADM' : broker;
}

function priceCell(value: number | undefined): string {
  return value == null ? EM_DASH : formatNumber(value, 2);
}

function lots(value: number): string {
  return String(Math.abs(value));
}

function toTicketRow(ticket: OmsOrder): TicketRow {
  const check = checkView(ticket);
  return {
    id: String(ticket.orderId),
    kind: 'ticket',
    children: [
      {
        id: `${ticket.orderId}-allocations`,
        kind: 'allocations',
        orderId: ticket.orderId,
        qty: Math.abs(ticket.qty),
        allocations: ticket.allocations ?? [],
      },
    ],
    ticket,
    orderId: ticket.orderId,
    orderNumber: dash(ticket.orderNumber),
    symbol: dash(ticket.bbTicker ?? ticket.instrumentDescription),
    ...contractCell(ticket),
    side: ticket.side ? SIDE_LABELS[ticket.side] : EM_DASH,
    qty: Math.abs(ticket.qty),
    orderType: dash(ticket.orderType),
    limit: priceCell(ticket.limitPrice),
    tif: dash(ticket.tif),
    brokerAccount: [brokerName(ticket.broker), ticket.executionAccount].filter(Boolean).join(' · ') || EM_DASH,
    status: omsOrderStatusLabel(ticket.status),
    check,
    selectable: check.group !== 'BLOCK',
  };
}

/** `8 + 2 = 10 of 10 lots.` and whether the instructions cover the ticket. */
function allocationSum(
  allocations: readonly OmsAllocation[],
  qty: number,
): { text: string; tone: 'success.main' | 'warning.main' | 'text.secondary' } {
  if (allocations.length === 0)
    return { text: `No allocation instructions recorded on-prem. 0 of ${lots(qty)} lots instructed.`, tone: 'text.secondary' };
  const parts = allocations.map((allocation) => Math.abs(allocation.instructionQty));
  const sum = parts.reduce((total, part) => total + part, 0);
  const equation = `${parts.join(' + ')} = ${sum} of ${lots(qty)} lots.`;
  const gap = Math.round((qty - sum) * 1e6) / 1e6;
  if (gap === 0) return { text: `${equation} The instructions add up.`, tone: 'success.main' };
  return {
    text: `${equation} The instructions do not add up: ${Math.abs(gap)} ${Math.abs(gap) === 1 ? 'lot' : 'lots'} ${gap > 0 ? 'short' : 'over'}.`,
    tone: 'warning.main',
  };
}

function panelHeight(row: AllocationRow): number {
  return PANEL_CHROME_PX + Math.max(1, row.allocations.length) * PANEL_LINE_PX;
}

function AllocationPanel({ row }: { row: AllocationRow }) {
  const sum = allocationSum(row.allocations, row.qty);
  const grid = { display: 'grid', gridTemplateColumns: 'minmax(160px, 1fr) 120px 90px', columnGap: 2, alignItems: 'center' } as const;
  return (
    <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, fontSize: 12.5, width: 'min(100%, 560px)', boxSizing: 'border-box' }}>
      <HFBox sx={SECTION_LABEL_SX}>Allocation instructions · clearing accounts where fills will go</HFBox>
      <HFBox role="table" aria-label={`Allocation instructions for ticket ${row.orderId}`}>
        <HFBox role="row" sx={{ ...grid, height: PANEL_LINE_PX, color: 'text.secondary', fontSize: 11.5, fontWeight: 700 }}>
          <HFBox role="columnheader">Clearing account</HFBox>
          <HFBox role="columnheader" sx={{ textAlign: 'right' }}>
            Instruction qty
          </HFBox>
          <HFBox role="columnheader" sx={{ textAlign: 'right' }}>
            Alloc. %
          </HFBox>
        </HFBox>
        {row.allocations.map((allocation, index) => (
          <HFBox
            key={`${row.id}-${index}`}
            role="row"
            sx={{ ...grid, height: PANEL_LINE_PX, borderTop: 1, borderColor: 'divider', fontVariantNumeric: 'tabular-nums' }}
          >
            <HFBox role="cell">
              {allocation.clearingAccount ? (
                <HFBox component="span" sx={{ fontWeight: 700 }}>
                  {allocation.clearingAccount}
                </HFBox>
              ) : (
                <HFChip tone="neutral" label="Unallocated" />
              )}
            </HFBox>
            <HFBox role="cell" sx={{ textAlign: 'right' }}>
              {lots(allocation.instructionQty)}
            </HFBox>
            <HFBox role="cell" sx={{ textAlign: 'right' }}>{`${allocation.allocPct.toFixed(2)}%`}</HFBox>
          </HFBox>
        ))}
      </HFBox>
      <HFBox role="status" sx={{ color: sum.tone, borderTop: 1, borderColor: 'divider', pt: 0.5 }}>
        {sum.text}
      </HFBox>
    </HFBox>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <HFBox component="dt" sx={{ color: 'text.secondary' }}>
        {label}
      </HFBox>
      <HFBox component="dd" sx={{ m: 0, textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere' }}>
        {children}
      </HFBox>
    </>
  );
}

function PreviewInspector({ row }: { row: TicketRow | null }) {
  if (!row) {
    return <HFBox sx={{ color: 'text.secondary', fontSize: 13 }}>No ticket to inspect.</HFBox>;
  }
  const { ticket } = row;
  const sum = allocationSum(ticket.allocations ?? [], row.qty);
  const allocationText = (ticket.allocations ?? [])
    .map((allocation) => `${allocation.clearingAccount ?? 'Unallocated'} ${lots(allocation.instructionQty)}`)
    .join(' · ');
  const hits = (ticket.checkHits ?? []).map((hit) => hit.checkName).join(', ');
  const groups: Array<{ label: string; fields: Array<[string, ReactNode]> }> = [
    {
      label: 'Instrument',
      fields: [
        ['BB ticker', row.symbol],
        ['Instrument', dash(ticket.instrumentDescription)],
        ['Exchange', dash(ticket.exchange)],
        ['Product', dash(ticket.product)],
        ['Contract', row.contractHint ? `${row.contract} (${row.contractHint})` : row.contract],
      ],
    },
    {
      label: 'Order',
      fields: [
        ['Side', row.side],
        ['Quantity', lots(row.qty)],
        ['Order type', row.orderType],
        ['Limit', row.limit],
        ['Stop', priceCell(ticket.stopPrice)],
        ['Time in force', row.tif],
        ['TIF date', ticket.tifDate ? formatDate(ticket.tifDate, 'medium') : EM_DASH],
      ],
    },
    {
      label: 'Routing',
      fields: [
        ['Broker', dash(brokerName(ticket.broker))],
        ['Account', dash(ticket.executionAccount)],
        ['Books', ticket.books.length > 0 ? ticket.books.join(' · ') : EM_DASH],
        ['Trader', dash(ticket.trader)],
        ['Order number', row.orderNumber],
        ['Placed on', ticket.placedOn ? formatUtcDateTime(ticket.placedOn) : EM_DASH],
      ],
    },
    {
      label: 'Check',
      fields: [
        ['Check', <HFChip key="check" tone={row.check.tone} label={row.check.label} />],
        ['Rule hits', hits || 'Not recorded on-prem'],
        ['Status', row.status],
        ['Allocations', allocationText ? `${allocationText}. ${sum.text}` : sum.text],
        ['Comments', dash(ticket.comments)],
      ],
    },
  ];
  return (
    <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <HFBox sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <HFBox component="h3" sx={{ m: 0, fontSize: 14, fontWeight: 800 }}>{`Selected preview ticket · ${row.orderId}`}</HFBox>
        <HFChip tone={row.check.tone} label={row.check.label} />
      </HFBox>
      <HFBox
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(1, minmax(0, 1fr))', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
          gap: 2,
        }}
      >
        {groups.map((group) => (
          <HFBox key={group.label} sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
            <HFBox sx={SECTION_LABEL_SX}>{group.label}</HFBox>
            <HFBox
              component="dl"
              aria-label={group.label}
              sx={{ m: 0, display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', columnGap: 1.5, rowGap: 0.75, fontSize: 12.5 }}
            >
              {group.fields.map(([label, value]) => (
                <Field key={label} label={label}>
                  {value}
                </Field>
              ))}
            </HFBox>
          </HFBox>
        ))}
      </HFBox>
    </HFBox>
  );
}

function options(values: string[]): Array<{ value: string; label: string }> {
  return [{ value: '', label: 'All' }, ...values.map((value) => ({ value, label: value }))];
}

function distinct(values: Array<string | undefined>): string[] {
  return [...new Set(values.filter((value): value is string => Boolean(value?.trim())))].sort((left, right) => left.localeCompare(right));
}

function EmptyTickets({ message }: { message: string }) {
  return <HFBox sx={{ px: 2, py: 3, color: 'text.secondary', fontSize: 14 }}>{message}</HFBox>;
}

/**
 * Order Preview on Create Order (OMS-CLOUD-026/027/028): staged tickets saved on-prem and not released.
 * Read once per open and on Refresh, never on a timer. Filters run on screen, so a filter never reads on-prem again.
 */
export function OrderPreview({ date, onRead }: { date?: string; onRead?: (ok: boolean) => void }) {
  const { request } = useHttp();
  const [tickets, setTickets] = useState<OmsOrder[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [book, setBook] = useState('');
  const [exchange, setExchange] = useState('');
  const [product, setProduct] = useState('');
  const [check, setCheck] = useState('');
  const [side, setSide] = useState('');
  const [search, setSearch] = useState('');
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<GridRowId>>(() => new Set());
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<number>>(() => new Set());
  const [inspectedId, setInspectedId] = useState<number | null>(null);
  const [shownDate, setShownDate] = useState(date);
  if (shownDate !== date) {
    setShownDate(date);
    setTickets(null);
    setExpandedIds(new Set());
    setSelectedIds(new Set());
    setInspectedId(null);
  }
  const onReadRef = useRef(onRead);
  onReadRef.current = onRead;

  useEffect(() => {
    let cancelled = false;
    setRefreshing(true);
    void request<OmsOrderList>(`/oms/preview-tickets${date ? `?date=${date}` : ''}`, { responseSchema: omsOrderListSchema }).then((result) => {
      if (cancelled) return;
      setRefreshing(false);
      if (result.status !== 200 || !result.data || !Array.isArray(result.data.orders)) {
        setTickets((current) => current ?? []);
        setFailed(true);
        onReadRef.current?.(false);
        return;
      }
      setTickets(result.data.orders);
      setFailed(false);
      onReadRef.current?.(true);
    });
    return () => {
      cancelled = true;
    };
  }, [date, reloadTick, request]);

  const rows = useMemo(() => (tickets ?? []).map(toTicketRow), [tickets]);
  const query = search.trim().toLowerCase();
  const shown = useMemo(
    () =>
      rows.filter(
        (row) =>
          (!book || row.ticket.books.includes(book)) &&
          (!exchange || row.ticket.exchange === exchange) &&
          (!product || row.ticket.product === product) &&
          (!check || row.check.group === check) &&
          (!side || row.ticket.side === side) &&
          (!query ||
            [String(row.orderId), row.ticket.orderNumber, row.ticket.bbTicker, row.ticket.instrumentDescription].some((value) =>
              value?.toLowerCase().includes(query),
            )),
      ),
    [book, check, exchange, product, query, rows, side],
  );
  const facets = useMemo(
    () => ({
      books: distinct(rows.flatMap((row) => row.ticket.books)),
      exchanges: distinct(rows.map((row) => row.ticket.exchange)),
      products: distinct(rows.map((row) => row.ticket.product)),
    }),
    [rows],
  );
  const selected = shown.filter((row) => row.selectable && selectedIds.has(row.orderId));
  const blocked = shown.filter((row) => !row.selectable);
  const inspected = shown.find((row) => row.orderId === inspectedId) ?? shown[0] ?? null;
  const settled = tickets !== null;
  const filtersActive = Boolean(book || exchange || product || check || side || query);
  const warnCount = rows.filter((row) => row.check.group === 'WARN').length;
  const blockCount = rows.filter((row) => row.check.group === 'BLOCK').length;
  const ticketWord = (count: number) => (count === 1 ? '1 ticket' : `${count} tickets`);
  const summary = [
    filtersActive ? `${shown.length} of ${ticketWord(rows.length)}` : ticketWord(rows.length),
    warnCount > 0 ? `${warnCount} Warn` : '',
    blockCount > 0 ? `${blockCount} Block` : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const blockedNote =
    blocked.length === 1
      ? `Ticket ${blocked[0]!.orderId} is ${blocked[0]!.check.label === 'Block' ? 'Block' : blocked[0]!.check.label.toLowerCase()}, so it cannot be selected.`
      : blocked.length > 1
        ? `${blocked.length} tickets are Block or not checked, so they cannot be selected.`
        : 'Block tickets cannot be selected.';

  const toggleSelected = (orderId: number, checked: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) next.add(orderId);
      else next.delete(orderId);
      return next;
    });
  };

  const rowTree = useMemo<HFDataTableRowTree<PreviewRow>>(
    () => ({
      getChildren: (row) => (row.kind === 'ticket' ? row.children : undefined),
      renderChildRow: (row) => (row.kind === 'allocations' ? <AllocationPanel row={row} /> : null),
      getToggleLabel: (row, open) => `${open ? 'Collapse' : 'Expand'} ticket ${row.orderId} allocation instructions`,
      expandedIds,
      onExpandedIdsChange: setExpandedIds,
    }),
    [expandedIds],
  );

  const columns = useMemo<Array<HFGridColDef<PreviewRow>>>(() => {
    const text = (field: keyof TicketRow, headerName: string, width: number, align?: 'right'): HFGridColDef<PreviewRow> => ({
      field,
      headerName,
      width,
      sortable: false,
      ...(align ? { align, headerAlign: align } : {}),
      valueGetter: (_value: unknown, row: PreviewRow) => (row.kind === 'ticket' ? row[field] : ''),
    });
    return [
      {
        field: 'select',
        headerName: '',
        width: 52,
        sortable: false,
        renderCell: ({ row }: { row: PreviewRow }) => {
          if (row.kind !== 'ticket') return null;
          return (
            <HFBox
              component="span"
              title={row.selectable ? 'Select for confirm or reject' : `${row.check.label} tickets cannot be selected`}
              sx={{ display: 'inline-flex', '& .MuiFormControlLabel-root': { m: 0 } }}
            >
              <HFCheckbox
                label=""
                size="small"
                checked={row.selectable && selectedIds.has(row.orderId)}
                disabled={!row.selectable}
                onChange={(checked) => toggleSelected(row.orderId, checked)}
                onClick={(event) => event.stopPropagation()}
                slotProps={{ input: { 'aria-label': `Select ticket ${row.orderId}` } }}
                sx={{ p: 0.5 }}
              />
            </HFBox>
          );
        },
      },
      {
        field: 'orderId',
        headerName: 'Ord. ID',
        width: 96,
        sortable: false,
        renderCell: ({ row }: { row: PreviewRow }) =>
          row.kind === 'ticket' ? (
            <HFBox
              component="button"
              type="button"
              aria-label={`Inspect ticket ${row.orderId}`}
              aria-pressed={inspected?.orderId === row.orderId}
              onClick={() => setInspectedId(row.orderId)}
              sx={{ p: 0, border: 0, bgcolor: 'transparent', color: 'primary.main', font: 'inherit', fontWeight: 700, cursor: 'pointer' }}
            >
              {row.orderId}
            </HFBox>
          ) : null,
      },
      text('orderNumber', 'Order number', 170),
      text('symbol', 'BB ticker', 140),
      {
        field: 'contract',
        headerName: 'Contract',
        width: 96,
        sortable: false,
        renderCell: ({ row }: { row: PreviewRow }) => {
          if (row.kind !== 'ticket') return null;
          return row.contractHint ? (
            <HFTooltip title={row.contractHint}>
              <HFBox component="span" aria-label={`${row.contract}, ${row.contractHint}`}>
                {row.contract}
              </HFBox>
            </HFTooltip>
          ) : (
            row.contract
          );
        },
      },
      text('side', 'Side', 72),
      text('qty', 'Qty', 72, 'right'),
      text('orderType', 'Type', 100),
      text('limit', 'Limit', 100, 'right'),
      text('tif', 'TIF', 90),
      text('brokerAccount', 'Broker · Account', 190),
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        sortable: false,
        renderCell: ({ row }: { row: PreviewRow }) => (row.kind === 'ticket' ? <HFChip tone="neutral" label={row.status} /> : null),
      },
      {
        field: 'check',
        headerName: 'Check',
        width: 120,
        sortable: false,
        renderCell: ({ row }: { row: PreviewRow }) => (row.kind === 'ticket' ? <HFChip tone={row.check.tone} label={row.check.label} /> : null),
      },
    ];
  }, [inspected?.orderId, selectedIds]);

  const panelHeights = useMemo(() => new Map(rows.map((row) => [row.children[0]!.id, panelHeight(row.children[0]!)])), [rows]);

  const resetFilters = () => {
    setBook('');
    setExchange('');
    setProduct('');
    setCheck('');
    setSide('');
    setSearch('');
  };

  return (
    <>
      <HFCard
        component="section"
        aria-label="Order Preview"
        aria-busy={!settled}
        sx={{ ...monitorPaneSx, '& .MuiCardContent-root': { minHeight: 0, overflow: 'hidden' } }}
      >
        <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minHeight: 0 }}>
          <HFBox sx={CHROME_SX}>
            <HFBox sx={CHROME_ROW_SX}>
              <HFBox sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
                <HFBox component="h2" sx={{ m: 0, fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em' }}>
                  Order Preview
                </HFBox>
                {settled && !failed ? (
                  <HFBox component="span" sx={{ fontSize: 13, color: 'text.secondary' }}>
                    {summary}
                  </HFBox>
                ) : null}
              </HFBox>
              <HFBox sx={{ ...CLUSTER_SX, ml: 'auto' }}>
                <HFButton size="small" sx={COMPACT_BUTTON_SX} onClick={resetFilters} disabled={!filtersActive && search === ''}>
                  Reset filters
                </HFButton>
                <HFButton
                  size="small"
                  variant="outlined"
                  sx={COMPACT_BUTTON_SX}
                  loading={settled && refreshing}
                  disabled={!settled}
                  onClick={() => setReloadTick((tick) => tick + 1)}
                >
                  Refresh
                </HFButton>
              </HFBox>
            </HFBox>
            <HFBox sx={FILTER_GRID_SX}>
              <HFTextField
                label="Search"
                placeholder="Ticker, Ord. ID, order number"
                size="small"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                slotProps={{ htmlInput: { maxLength: 64 } }}
                sx={{ ...FILTER_FIELD_SX, width: '100%' }}
              />
              <HFSelect
                label="Book"
                size="small"
                displayEmpty
                value={book}
                onChange={setBook}
                options={options(facets.books)}
                formControlSx={FILTER_FIELD_SX}
              />
              <HFSelect
                label="Exchange"
                size="small"
                displayEmpty
                value={exchange}
                onChange={setExchange}
                options={options(facets.exchanges)}
                formControlSx={FILTER_FIELD_SX}
              />
              <HFSelect
                label="Product"
                size="small"
                displayEmpty
                value={product}
                onChange={setProduct}
                options={options(facets.products)}
                formControlSx={FILTER_FIELD_SX}
              />
              <HFSelect
                label="Check"
                size="small"
                displayEmpty
                value={check}
                onChange={setCheck}
                options={CHECK_OPTIONS}
                formControlSx={FILTER_FIELD_SX}
              />
              <HFSelect
                label="Side"
                size="small"
                displayEmpty
                value={side}
                onChange={setSide}
                options={SIDE_OPTIONS}
                formControlSx={FILTER_FIELD_SX}
              />
            </HFBox>
          </HFBox>

          <HFBox sx={{ position: 'relative', flex: 1, minHeight: 160 }}>
            {settled ? (
              <HFBox sx={{ position: 'absolute', inset: 0, minHeight: 0, overflow: 'hidden' }}>
                <HFDataTable<PreviewRow>
                  rows={shown}
                  columns={columns}
                  getRowId={(row) => row.id}
                  rowTree={rowTree}
                  getRowHeight={({ id }) => panelHeights.get(String(id)) ?? TICKET_ROW_PX}
                  getRowClassName={({ row }) => (row.kind === 'ticket' && row.orderId === inspected?.orderId ? 'preview-row--inspected' : '')}
                  onRowClick={({ row }: { row: PreviewRow }) => {
                    if (row.kind === 'ticket') setInspectedId(row.orderId);
                  }}
                  columnSearch={false}
                  disableColumnMenu
                  hideFooter
                  fill
                  autoHeight={false}
                  autosizeOnMount={false}
                  disableColumnVirtualization
                  sx={{ '& .preview-row--inspected': { bgcolor: (theme) => hfAlpha(theme.palette.primary.main, 0.08) } }}
                  slots={{
                    noRowsOverlay: () => (
                      <EmptyTickets
                        message={
                          failed ? 'Order Preview unavailable.' : rows.length > 0 ? 'No tickets match these filters.' : 'No tickets in Order Preview.'
                        }
                      />
                    ),
                  }}
                />
              </HFBox>
            ) : (
              <TableSkeleton testId="order-preview-skeleton" />
            )}
          </HFBox>

          <HFBox
            role="group"
            aria-label="Bulk actions"
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              borderTop: 1,
              borderColor: 'divider',
              pt: 1.5,
              fontSize: 13,
            }}
          >
            <HFBox component="span" sx={{ fontWeight: 700, minWidth: 130 }}>{`${ticketWord(selected.length)} selected`}</HFBox>
            {['Confirm selected as good', 'Confirm all passing', 'Reject selected'].map((label) => (
              <HFTooltip key={label} title={WRITE_STEP}>
                <HFBox component="span">
                  <HFButton size="small" variant="outlined" sx={COMPACT_BUTTON_SX} disabled>
                    {label}
                  </HFButton>
                </HFBox>
              </HFTooltip>
            ))}
            <HFBox component="span" sx={{ color: 'text.secondary' }}>
              {`Confirm and Reject stay off until the write step. ${blockedNote} Warn tickets will ask for an acknowledgement on confirm.`}
            </HFBox>
          </HFBox>
        </HFBox>
      </HFCard>

      <HFCard component="section" aria-label="Selected preview ticket" sx={{ flexShrink: 0, maxHeight: '40%', overflow: 'auto' }}>
        {settled ? (
          <PreviewInspector row={inspected} />
        ) : (
          <HFBox data-testid="preview-inspector-skeleton" aria-hidden sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[40, 100, 100, 70].map((width, index) => (
              <HFSkeleton key={index} variant="rounded" height={12} sx={{ width: `${width}%` }} />
            ))}
          </HFBox>
        )}
      </HFCard>
    </>
  );
}
